import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiTrash2,
  FiPlusCircle,
  FiSearch,
  FiInfo,
  FiAlertCircle,
  FiCheckCircle,
  FiFileText,
  FiDollarSign,
} from "react-icons/fi";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import Checkbox from "../../../components/BaseUI/Checkbox";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Toggle from "../../../components/BaseUI/Toggle";
import { approvalWorkflowsApi } from "../../../api/approvalWorkflows";
import axios from "axios";

const approverKey = (item) => `${item.type}:${item.id}`;

const API_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:3100";

/** Must stay in sync with backend APPROVAL_ENTITY_TYPES / approvals registry. */
const ENTITY_TYPE_OPTIONS = [
  {
    value: "order",
    label: "Teklif",
    description: "Müşteri teklifleri için onay akışı",
    icon: FiFileText,
  },
  {
    value: "sale",
    label: "Sipariş",
    description: "Sipariş kayıtları için onay akışı",
    icon: FiDollarSign,
  },
];

const REJECT_STATUS_OPTIONS = [
  { value: "rejected", label: "Reddedildi" },
  { value: "revision_required", label: "Revizyon Gerekli" },
  { value: "cancelled", label: "İptal Edildi" },
  { value: "__custom__", label: "Özel değer girin..." },
];

/** Step code from label: Turkish chars → ASCII, words joined with `_`. */
const generateStepKey = (text) => {
  if (!text) return "";
  return text
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ş/g, "S")
    .replace(/İ/g, "I")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
};

const emptyStep = (stepNumber) => ({
  stepNumber,
  stepKey: "",
  label: "",
  description: "",
  approvers: [],
  notificationRecipients: [],
  approveActionLabel: "Onayla",
  approveModalTitle: "",
  approveModalMessage: "",
  rejectActionLabel: "Reddet",
  rejectModalTitle: "Reddet",
  rejectModalMessage: "",
  onApproveStatus: "",
  onRejectStatus: "rejected",
  approveLogMessage: "",
  rejectLogMessage: "",
  requireUserConfirmAfter: false,
  unlocksDelivery: false,
  allowFileUpload: false,
});

function ApproverPicker({
  approvers = [],
  onChange,
  allUsers = [],
  allProfiles = [],
}) {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("users");

  const toggleApprover = (type, id, label) => {
    const exists = approvers.find((a) => a.type === type && a.id === id);
    if (exists) {
      onChange(approvers.filter((a) => !(a.type === type && a.id === id)));
    } else {
      onChange([...approvers, { type, id, label }]);
    }
  };

  const filteredUsers = allUsers.filter(
    (u) =>
      !search ||
      (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase()),
  );
  const filteredProfiles = allProfiles.filter(
    (p) =>
      !search || (p.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const renderList = (items, type) => (
    <div style={{ maxHeight: 280, overflowY: "auto" }}>
      {items.map((item, index) => {
        const id = String(item._id);
        const isSelected = approvers.some(
          (a) => a.type === type && a.id === id,
        );
        const subtitle = type === "user" ? item.email : item.description;
        return (
          <div
            key={id}
            role="button"
            tabIndex={0}
            onClick={() => toggleApprover(type, id, item.name)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleApprover(type, id, item.name);
              }
            }}
            className="d-flex align-items-center gap-3"
            style={{
              cursor: "pointer",
              padding: "12px 14px",
              backgroundColor: isSelected ? "#f1f5f9" : "transparent",
              borderBottom:
                index === items.length - 1 ? "none" : "1px solid #eef2f7",
            }}
          >
            <Checkbox isChecked={isSelected} />
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div
                className="text-truncate"
                style={{
                  fontSize: "0.9rem",
                  fontWeight: isSelected ? 600 : 500,
                  color: "#0f172a",
                  lineHeight: 1.35,
                }}
              >
                {item.name}
              </div>
              {subtitle && (
                <div
                  className="text-truncate"
                  style={{
                    fontSize: "0.8rem",
                    color: "#64748b",
                    marginTop: 2,
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {items.length === 0 && (
        <div
          style={{
            padding: "32px 16px",
            textAlign: "center",
            fontSize: "0.875rem",
            color: "#94a3b8",
          }}
        >
          {search ? "Eşleşen kayıt yok." : "Kayıt bulunamadı."}
        </div>
      )}
    </div>
  );

  const tabs = [
    {
      key: "users",
      label: "Kullanıcılar",
      count: approvers.filter((a) => a.type === "user").length,
    },
    {
      key: "profiles",
      label: "Roller",
      count: approvers.filter((a) => a.type === "profile").length,
    },
  ];

  const currentType = activeTab === "users" ? "user" : "profile";
  const currentItems = activeTab === "users" ? filteredUsers : filteredProfiles;
  const allVisibleSelected =
    currentItems.length > 0 &&
    currentItems.every((item) =>
      approvers.some(
        (a) => a.type === currentType && a.id === String(item._id),
      ),
    );

  const selectAllVisible = () => {
    const next = [...approvers];
    currentItems.forEach((item) => {
      const id = String(item._id);
      if (!next.some((a) => a.type === currentType && a.id === id)) {
        next.push({ type: currentType, id, label: item.name });
      }
    });
    onChange(next);
  };

  const unselectAllVisible = () => {
    const ids = new Set(currentItems.map((item) => String(item._id)));
    onChange(
      approvers.filter((a) => !(a.type === currentType && ids.has(a.id))),
    );
  };

  const toolbar = (
    <div
      className="d-flex align-items-center justify-content-between"
      style={{
        padding: "8px 14px",
        borderBottom: "1px solid #eef2f7",
        backgroundColor: "#fafafa",
      }}
    >
      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
        {currentItems.length} kayıt
      </span>
      <button
        type="button"
        disabled={!currentItems.length}
        onClick={() =>
          allVisibleSelected ? unselectAllVisible() : selectAllVisible()
        }
        style={{
          border: "none",
          background: "transparent",
          color: currentItems.length ? "#334155" : "#94a3b8",
          fontSize: "0.8rem",
          fontWeight: 600,
          padding: "4px 6px",
          cursor: currentItems.length ? "pointer" : "default",
        }}
      >
        {allVisibleSelected ? "Seçimi kaldır" : "Tümünü seç"}
      </button>
    </div>
  );

  return (
    <div
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        backgroundColor: "#fff",
      }}
    >
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>
        <Input
          placeholder="Kullanıcı veya rol ara..."
          value={search}
          onChange={setSearch}
          iconBefore={<FiSearch size={15} />}
        />
      </div>

      <Tabs
        selected={activeTab === "users" ? 1 : 2}
        onChange={(idx) => {
          const next = idx === 0 ? "users" : "profiles";
          if (next !== activeTab) setActiveTab(next);
        }}
      >
        <TabList>
          {tabs.map(({ key, label, count }) => (
            <Tab key={key}>
              {label}
              {count > 0 ? ` (${count})` : ""}
            </Tab>
          ))}
        </TabList>
        <TabPanel>
          <Panel>
            {toolbar}
            {renderList(filteredUsers, "user")}
          </Panel>
          <Panel>
            {toolbar}
            {renderList(filteredProfiles, "profile")}
          </Panel>
        </TabPanel>
      </Tabs>

      <div
        style={{
          borderTop: "1px solid #e2e8f0",
          padding: "12px 14px",
          backgroundColor: "#f8fafc",
        }}
      >
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div style={{ minWidth: 0, flex: 1, display: "grid", gap: 8 }}>
            {[
              {
                title: "Kullanıcılar",
                items: approvers.filter((a) => a.type === "user"),
              },
              {
                title: "Roller",
                items: approvers.filter((a) => a.type === "profile"),
              },
            ].map(({ title, items }) => (
              <div key={title} style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#475569",
                    marginBottom: 2,
                  }}
                >
                  {title}
                  {items.length ? ` (${items.length})` : ""}
                </div>
                <div
                  title={
                    items.length
                      ? items.map((a) => a.label || a.id).join(", ")
                      : undefined
                  }
                  style={{
                    fontSize: "0.8rem",
                    color: items.length ? "#64748b" : "#94a3b8",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {items.length
                    ? items.map((a) => a.label || a.id).join(" · ")
                    : "Seçim yok"}
                </div>
              </div>
            ))}
          </div>
          {approvers.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                flexShrink: 0,
                border: "1px solid #cbd5e1",
                backgroundColor: "#fff",
                color: "#475569",
                fontSize: "0.8rem",
                fontWeight: 500,
                padding: "6px 12px",
                borderRadius: 4,
                cursor: "pointer",
                whiteSpace: "nowrap",
                alignSelf: "center",
              }}
            >
              Tümünü kaldır
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationRecipientPicker({
  approvers = [],
  recipients = [],
  onChange,
}) {
  const recipientKeys = new Set(recipients.map(approverKey));

  const toggleRecipient = (approver) => {
    const key = approverKey(approver);
    if (recipientKeys.has(key)) {
      onChange(recipients.filter((item) => approverKey(item) !== key));
      return;
    }
    onChange([...recipients, { ...approver }]);
  };

  if (approvers.length === 0) {
    return (
      <div
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 8,
          backgroundColor: "#fff",
          padding: "20px 16px",
          textAlign: "center",
          fontSize: "0.875rem",
          color: "#94a3b8",
        }}
      >
        Önce onaylayıcı seçin
      </div>
    );
  }

  const groups = [
    {
      title: "Kullanıcılar",
      items: approvers.filter((item) => item.type !== "profile"),
    },
    {
      title: "Roller",
      items: approvers.filter((item) => item.type === "profile"),
    },
  ].filter((group) => group.items.length > 0);

  return (
    <div
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        backgroundColor: "#fff",
        maxHeight: 300,
        overflowY: "auto",
      }}
    >
      {groups.map((group, groupIndex) => (
        <div key={group.title}>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "8px 14px",
              backgroundColor: "#fafafa",
              borderBottom: "1px solid #eef2f7",
              borderTop: groupIndex === 0 ? "none" : "1px solid #eef2f7",
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "#64748b",
            }}
          >
            <span>{group.title}</span>
            <span style={{ fontWeight: 500, textTransform: "none" }}>
              {
                group.items.filter((item) =>
                  recipientKeys.has(approverKey(item)),
                ).length
              }
              /{group.items.length}
            </span>
          </div>
          {group.items.map((approver, index) => {
            const selected = recipientKeys.has(approverKey(approver));
            return (
              <div
                key={approverKey(approver)}
                role="button"
                tabIndex={0}
                onClick={() => toggleRecipient(approver)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleRecipient(approver);
                  }
                }}
                className="d-flex align-items-center gap-3"
                style={{
                  cursor: "pointer",
                  padding: "10px 14px",
                  backgroundColor: selected ? "#f8fafc" : "transparent",
                  borderBottom:
                    index === group.items.length - 1
                      ? "none"
                      : "1px solid #eef2f7",
                }}
              >
                <Checkbox isChecked={selected} />
                <span
                  className="text-truncate"
                  style={{
                    minWidth: 0,
                    fontSize: "0.9rem",
                    fontWeight: selected ? 600 : 500,
                    color: selected ? "#0f172a" : "#64748b",
                  }}
                >
                  {approver.label || approver.id}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ActiveStepForm({ step, onChange, allUsers, allProfiles, entityType }) {
  const update = (field, val) => onChange({ ...step, [field]: val });

  const handleLabelChange = (v) => {
    const generatedKey = generateStepKey(v);
    onChange({
      ...step,
      label: v,
      stepKey: generatedKey,
      onApproveStatus: generatedKey ? `${generatedKey}_approved` : "",
    });
  };

  const knownRejectStatus = REJECT_STATUS_OPTIONS.find(
    (o) => o.value !== "__custom__" && o.value === step.onRejectStatus,
  );
  const isCustomReject = !knownRejectStatus && !!step.onRejectStatus;
  const [rejectSelectVal, setRejectSelectVal] = useState(
    isCustomReject ? "__custom__" : step.onRejectStatus || "rejected",
  );

  const handleRejectSelectChange = (val) => {
    setRejectSelectVal(val);
    if (val !== "__custom__") {
      update("onRejectStatus", val);
    }
  };

  const sectionStyle = {
    borderRadius: 10,
    padding: "1.25rem",
    border: "1px solid #e2e8f0",
  };
  return (
    <div className="bg-white rounded p-4">
      {/* Adım Bilgileri */}
      <div className="mb-4">
        <p
          className="fw-semibold mb-2"
          style={{
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#64748b",
          }}
        >
          Adım Bilgileri
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1.25rem",
          }}
        >
          <div>
            <Input
              label="Adım Adı"
              required
              value={step.label}
              onChange={handleLabelChange}
              placeholder="Örn. Yönetici Kontrolü"
            />
          </div>
          <div>
            <Input
              label="Açıklama"
              value={step.description}
              onChange={(v) => update("description", v)}
              placeholder="Bu adımda onaylayıcı ne yapmalı? Kısaca açıklayın"
            />
          </div>
        </div>
      </div>

      {/* Onay / Red Panelleri */}
      <div className="mb-4">
        <p
          className="fw-semibold mb-2"
          style={{
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#64748b",
          }}
        >
          Onay ve Red Ayarları
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1.25rem",
          }}
        >
          {/* Onay Paneli */}
          <div style={{ ...sectionStyle, borderLeftWidth: 3 }}>
            <p
              className="fw-semibold mb-3"
              style={{ fontSize: "0.85rem", color: "#16a34a" }}
            >
              Onaylama
            </p>
            <div className="mb-3">
              <Input
                label="Buton Metni"
                required
                value={step.approveActionLabel}
                onChange={(v) => update("approveActionLabel", v)}
                placeholder="Onayla"
              />
              <small className="text-muted">
                Onay butonunda görünecek yazı
              </small>
            </div>
            <div className="mb-3">
              <Input
                label="Onay Penceresi Başlığı"
                value={step.approveModalTitle}
                onChange={(v) => update("approveModalTitle", v)}
                placeholder="Boş bırakılırsa buton metni kullanılır"
              />
            </div>
            <div>
              <Input
                label="Onay Penceresi Mesajı"
                value={step.approveModalMessage}
                onChange={(v) => update("approveModalMessage", v)}
                placeholder="Boş bırakılırsa varsayılan mesaj kullanılır"
              />
            </div>
          </div>

          {/* Red Paneli */}
          <div style={{ ...sectionStyle, borderLeftWidth: 3 }}>
            <p
              className="fw-semibold mb-3"
              style={{ fontSize: "0.85rem", color: "#dc2626" }}
            >
              Reddetme
            </p>
            <div className="mb-3">
              <Input
                label="Buton Metni"
                value={step.rejectActionLabel}
                onChange={(v) => update("rejectActionLabel", v)}
                placeholder="Reddet"
              />
              <small className="text-muted">
                Reddet butonunda görünecek yazı
              </small>
            </div>
            <div className="mb-3">
              <Input
                label="Red Penceresi Başlığı"
                value={step.rejectModalTitle}
                onChange={(v) => update("rejectModalTitle", v)}
                placeholder="Boş bırakılırsa buton metni kullanılır"
              />
            </div>
            <div className="mb-3">
              <Input
                label="Red Penceresi Mesajı"
                value={step.rejectModalMessage}
                onChange={(v) => update("rejectModalMessage", v)}
                placeholder="Boş bırakılırsa varsayılan mesaj kullanılır"
              />
            </div>
            <div>
              <Select
                label="Reddedilince Kayıt Durumu Ne Olsun?"
                unique
                options={REJECT_STATUS_OPTIONS}
                selected={[rejectSelectVal]}
                onSelected={(values) => {
                  const v = values?.[0]?.value;
                  if (v) handleRejectSelectChange(v);
                }}
                placeholder="Durum seçin"
              />
              {rejectSelectVal === "__custom__" && (
                <div className="mt-2">
                  <Input
                    label="Özel durum kodu"
                    value={isCustomReject ? step.onRejectStatus : ""}
                    onChange={(v) => update("onRejectStatus", v)}
                    placeholder="Özel durum kodu girin"
                  />
                </div>
              )}
              <small className="text-muted">
                Bu adım reddedildiğinde sipariş hangi duruma geçecek?
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Kullanıcı Doğrulaması — order only (sale has no user-confirm UI) */}
      {entityType !== "sale" && (
        <div
          className="mb-4 p-3 rounded"
          style={{ border: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p className="fw-semibold mb-1" style={{ fontSize: "0.9rem" }}>
                Kullanıcı Doğrulaması
              </p>
              <small className="text-muted">
                Bu seçenek açıksa, adım onaylandıktan sonra siparişi oluşturan
                kişiden ek onay istenir.
              </small>
            </div>
            <Toggle
              isChecked={!!step.requireUserConfirmAfter}
              onChecked={(v) => update("requireUserConfirmAfter", v)}
            />
          </div>
          {step.requireUserConfirmAfter && (
            <div
              className="mt-3 p-2 rounded d-flex align-items-start gap-2"
              style={{
                backgroundColor: "#eff6ff",
                border: "1px solid #bfdbfe",
              }}
            >
              <FiInfo
                size={15}
                style={{ color: "#3b82f6", marginTop: 2, minWidth: 15 }}
              />
              <small style={{ color: "#1d4ed8", lineHeight: 1.5 }}>
                Bu adım onaylandıktan sonra, bir sonraki adıma geçmeden önce
                siparişi oluşturan kullanıcıdan doğrulama istenecektir.
              </small>
            </div>
          )}
        </div>
      )}

      {entityType === "sale" && (
        <div
          className="mb-4 p-3 rounded"
          style={{ border: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p className="fw-semibold mb-1" style={{ fontSize: "0.9rem" }}>
                Sevkiyat İzni
              </p>
              <small className="text-muted">
                Bu adım onaylanmadan sipariş için sevkiyat oluşturulamaz.
                Yalnızca son adımda etkinleştirilmelidir.
              </small>
            </div>
            <Toggle
              isChecked={!!step.unlocksDelivery}
              onChecked={(value) => update("unlocksDelivery", value)}
            />
          </div>
        </div>
      )}

      <div
        className="mb-4 p-3 rounded"
        style={{ border: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <p className="fw-semibold mb-1" style={{ fontSize: "0.9rem" }}>
              Dosya Yükleme
            </p>
            <small className="text-muted">
              Onaylayan kişinin (yönetici veya kullanıcı doğrulaması) bu adımda
              bir görsel veya belge eklemesine izin verir.
            </small>
          </div>
          <Toggle
            isChecked={!!step.allowFileUpload}
            onChecked={(value) => update("allowFileUpload", value)}
          />
        </div>
      </div>

      {/* Onaylayacak Kişiler */}
      <div>
        <div className="d-flex align-items-center gap-2 mb-3">
          <p
            className="fw-semibold mb-0"
            style={{
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#64748b",
            }}
          >
            Onaylayacak Kişiler
          </p>
          {(step.approvers || []).length === 0 && (
            <span
              className="d-flex align-items-center gap-1"
              style={{ color: "#ef4444", fontSize: "0.75rem" }}
            >
              <FiAlertCircle size={13} /> En az 1 kişi veya rol seçmelisiniz
            </span>
          )}
          {(step.approvers || []).length > 0 && (
            <span
              className="d-flex align-items-center gap-1"
              style={{ color: "#22c55e", fontSize: "0.75rem" }}
            >
              <FiCheckCircle size={13} /> {step.approvers.length} kişi / rol
              seçildi
            </span>
          )}
        </div>
        <ApproverPicker
          approvers={step.approvers || []}
          onChange={(v) => {
            const previousKeys = new Set(
              (step.approvers || []).map(approverKey),
            );
            const nextKeys = new Set(v.map(approverKey));
            const keptRecipients = (step.notificationRecipients || []).filter(
              (item) => nextKeys.has(approverKey(item)),
            );
            const keptKeys = new Set(keptRecipients.map(approverKey));
            const addedApprovers = v.filter(
              (item) =>
                !previousKeys.has(approverKey(item)) &&
                !keptKeys.has(approverKey(item)),
            );
            onChange({
              ...step,
              approvers: v,
              notificationRecipients: [
                ...keptRecipients,
                ...addedApprovers.map((item) => ({ ...item })),
              ],
            });
          }}
          allUsers={allUsers || []}
          allProfiles={allProfiles || []}
        />

        <div className="mt-4">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
            <div className="d-flex align-items-center gap-2">
              <p
                className="fw-semibold mb-0"
                style={{
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "#64748b",
                }}
              >
                Bildirim Alacaklar
              </p>
              {(step.approvers || []).length > 0 &&
                ((step.notificationRecipients || []).length === 0 ? (
                  <span
                    className="d-flex align-items-center gap-1"
                    style={{ color: "#ef4444", fontSize: "0.75rem" }}
                  >
                    <FiAlertCircle size={13} /> En az 1 kişi seçin
                  </span>
                ) : (
                  <span
                    className="d-flex align-items-center gap-1"
                    style={{ color: "#22c55e", fontSize: "0.75rem" }}
                  >
                    <FiCheckCircle size={13} />{" "}
                    {(step.notificationRecipients || []).length} /{" "}
                    {(step.approvers || []).length} seçildi
                  </span>
                ))}
            </div>
            {(step.approvers || []).length > 0 && (
              <button
                type="button"
                onClick={() =>
                  update(
                    "notificationRecipients",
                    (step.notificationRecipients || []).length ===
                      (step.approvers || []).length
                      ? []
                      : (step.approvers || []).map((item) => ({ ...item })),
                  )
                }
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#334155",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  padding: "2px 4px",
                  cursor: "pointer",
                }}
              >
                {(step.notificationRecipients || []).length ===
                (step.approvers || []).length
                  ? "Seçimi kaldır"
                  : "Tümünü seç"}
              </button>
            )}
          </div>
          <small className="text-muted d-block mb-2">
            Yalnızca seçili onaylayıcılara bildirim gönderilir.
          </small>
          <NotificationRecipientPicker
            approvers={step.approvers || []}
            recipients={step.notificationRecipients || []}
            onChange={(v) => update("notificationRecipients", v)}
          />
        </div>
      </div>
    </div>
  );
}

function WorkflowForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  useTitle(isEdit ? "Onay Akışını Düzenle" : "Yeni Onay Akışı");

  const { getToken, user } = useAuth();
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const [form, setForm] = useState({
    name: "",
    entityType: "",
    description: "",
    steps: [],
    isActive: true,
  });

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [draggedStepIdx, setDraggedStepIdx] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedStepIdx(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (draggedStepIdx === null || draggedStepIdx === targetIdx) return;

    const newSteps = [...form.steps];
    const draggedStep = newSteps[draggedStepIdx];

    newSteps.splice(draggedStepIdx, 1);
    newSteps.splice(targetIdx, 0, draggedStep);

    newSteps.forEach((s, i) => {
      s.stepNumber = i + 1;
    });

    setForm((f) => ({ ...f, steps: newSteps }));

    if (activeStepIndex === draggedStepIdx) {
      setActiveStepIndex(targetIdx);
    } else if (
      activeStepIndex >= Math.min(draggedStepIdx, targetIdx) &&
      activeStepIndex <= Math.max(draggedStepIdx, targetIdx)
    ) {
      if (draggedStepIdx < targetIdx) {
        setActiveStepIndex(activeStepIndex - 1);
      } else {
        setActiveStepIndex(activeStepIndex + 1);
      }
    }
    setDraggedStepIdx(null);
  };

  const [allProfiles, setAllProfiles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [toasts, setToasts] = useState([]);

  const updateField = (field, val) => {
    setIsDirty(true);
    setForm((f) => ({ ...f, [field]: val }));
  };

  const addToast = (message, appearance = "success") => {
    const tId = Date.now();
    setToasts((t) => [...t, { id: tId, message, appearance }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== tId)), 4000);
  };

  useEffect(() => {
    if (form.steps.length > 0 && activeStepIndex >= form.steps.length) {
      setActiveStepIndex(Math.max(0, form.steps.length - 1));
    }
  }, [form.steps.length, activeStepIndex]);

  // Load users/profiles once — must NOT reset workflow form on user refresh
  useEffect(() => {
    let cancelled = false;
    async function loadReferenceData() {
      try {
        const headers = authHeaders();
        const [usersRes, profilesRes] = await Promise.all([
          axios.get(`${API_URL}/api/users`, headers),
          axios.get(`${API_URL}/api/user-profiles`, headers),
        ]);
        if (cancelled) return;

        const companyId =
          user?.companyId ||
          user?.company?.id ||
          user?.company?._id ||
          user?.company;
        const fetchedUsers = usersRes.data?.users || [];
        const filteredUsers = fetchedUsers.filter((u) => {
          if (u.status !== "active") return false;
          if (companyId) {
            const uCompanyId =
              u.companyId || u.company?.id || u.company?._id || u.company;
            return String(uCompanyId) === String(companyId);
          }
          return true;
        });

        setAllUsers(filteredUsers);
        setAllProfiles(profilesRes.data?.profiles || []);
      } catch (error) {
        console.error("Reference data load error:", error);
        if (!cancelled) {
          addToast("Kullanıcı ve profil listesi yüklenemedi.", "danger");
        }
      }
    }
    loadReferenceData();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, user?._id, user?.companyId]);

  // Load workflow only when route id changes — prevents wiping edits on auth refresh
  useEffect(() => {
    if (!isEdit) return undefined;

    let cancelled = false;
    async function loadWorkflow() {
      setLoading(true);
      try {
        const res = await approvalWorkflowsApi.getById(id);
        if (cancelled) return;

        const wf = res?.workflow || res;
        setForm({
          name: wf.name || "",
          entityType: wf.entityType || "",
          description: wf.description || "",
          steps: (wf.steps || []).map((s) => ({
            stepNumber: s.stepNumber,
            stepKey: s.stepKey || "",
            label: s.label || "",
            description: s.description || "",
            approvers: (s.approvers || []).map((a) => ({ ...a })),
            notificationRecipients:
              s.notificationRecipients === undefined
                ? (s.approvers || []).map((a) => ({ ...a }))
                : (s.notificationRecipients || []).map((a) => ({ ...a })),
            approveActionLabel: s.approveActionLabel || "Onayla",
            approveModalTitle: s.approveModalTitle || "",
            approveModalMessage: s.approveModalMessage || "",
            rejectActionLabel: s.rejectActionLabel || "Reddet",
            rejectModalTitle: s.rejectModalTitle || "Reddet",
            rejectModalMessage: s.rejectModalMessage || "",
            onApproveStatus: s.onApproveStatus || "",
            onRejectStatus: s.onRejectStatus || "rejected",
            approveLogMessage: s.approveLogMessage || "",
            rejectLogMessage: s.rejectLogMessage || "",
            requireUserConfirmAfter: !!s.requireUserConfirmAfter,
            unlocksDelivery: !!s.unlocksDelivery,
            allowFileUpload: !!s.allowFileUpload,
          })),
          isActive: wf.isActive !== false,
        });
        setIsDirty(false);
      } catch (error) {
        console.error("Workflow load error:", error);
        if (!cancelled) {
          addToast("Onay akışı yüklenemedi.", "danger");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadWorkflow();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const addStep = () => {
    const nextNum =
      form.steps.length > 0
        ? Math.max(...form.steps.map((s) => s.stepNumber)) + 1
        : 1;

    const label = `Adım ${nextNum}`;
    const stepKey = generateStepKey(label);

    setIsDirty(true);
    setForm((f) => ({
      ...f,
      steps: [
        ...f.steps,
        {
          stepNumber: nextNum,
          stepKey,
          label,
          description: "",
          approvers: [],
          notificationRecipients: [],
          approveActionLabel: "Onayla",
          approveModalTitle: "",
          approveModalMessage: "",
          rejectActionLabel: "Reddet",
          rejectModalTitle: "Reddet",
          rejectModalMessage: "",
          onApproveStatus: stepKey ? `${stepKey}_approved` : "",
          onRejectStatus: "rejected",
          requireUserConfirmAfter: false,
          unlocksDelivery: false,
          allowFileUpload: false,
        },
      ],
    }));
    setActiveStepIndex(form.steps.length);
  };

  const updateStep = (idx, stepData) => {
    setIsDirty(true);
    setForm((f) => {
      const newSteps = [...f.steps];
      newSteps[idx] = stepData;
      return { ...f, steps: newSteps };
    });
  };

  const removeStep = (idx) => {
    setIsDirty(true);
    setForm((f) => {
      const newSteps = f.steps.filter((_, i) => i !== idx);
      newSteps.forEach((s, i) => {
        s.stepNumber = i + 1;
      });
      return { ...f, steps: newSteps };
    });
    setActiveStepIndex((current) => {
      const nextLen = form.steps.length - 1;
      if (current >= nextLen) return Math.max(0, nextLen - 1);
      if (current > idx) return current - 1;
      return current;
    });
  };

  const moveStep = (idx, dir) => {
    if (idx + dir < 0 || idx + dir >= form.steps.length) return;
    setIsDirty(true);
    setForm((f) => {
      const newSteps = [...f.steps];
      const temp = newSteps[idx];
      newSteps[idx] = newSteps[idx + dir];
      newSteps[idx + dir] = temp;
      newSteps.forEach((s, i) => {
        s.stepNumber = i + 1;
      });
      return { ...f, steps: newSteps };
    });
    setActiveStepIndex(idx + dir);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      addToast("Akış adı zorunludur.", "danger");
      return;
    }
    if (!form.entityType) {
      addToast("Lütfen uygulanacak modülü seçin.", "danger");
      return;
    }
    if (form.steps.length === 0) {
      addToast("En az bir onay adımı eklemelisiniz.", "danger");
      return;
    }
    if (form.entityType === "sale") {
      const gates = form.steps.filter((step) => step.unlocksDelivery);
      const finalStep = [...form.steps].sort(
        (a, b) => b.stepNumber - a.stepNumber,
      )[0];
      if (gates.length !== 1) {
        addToast(
          "Sipariş akışında tam olarak bir sevkiyat izni adımı seçmelisiniz.",
          "danger",
        );
        return;
      }
      if (gates[0].stepKey !== finalStep.stepKey) {
        addToast("Sevkiyat izni yalnızca son adımda olabilir.", "danger");
        return;
      }
    }

    for (const s of form.steps) {
      if (!s.label.trim()) {
        addToast(`Adım ${s.stepNumber}: etiket zorunludur`, "danger");
        return;
      }
      if (!s.stepKey.trim()) {
        addToast(`Adım ${s.stepNumber}: stepKey zorunludur`, "danger");
        return;
      }
      if (!s.approvers || s.approvers.length === 0) {
        addToast(
          `Adım ${s.stepNumber}: En az bir onaylayıcı (Kullanıcı veya Profil) seçmelisiniz.`,
          "danger",
        );
        return;
      }
      if (!s.notificationRecipients || s.notificationRecipients.length === 0) {
        addToast(
          `Adım ${s.stepNumber}: En az bir bildirim alıcısı seçmelisiniz.`,
          "danger",
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        entityType: form.entityType,
        description: form.description,
        steps: form.steps.map((s) => ({
          ...s,
          onApproveStatus: s.stepKey ? `${s.stepKey}_approved` : "",
          requireUserConfirmAfter:
            form.entityType === "sale" ? false : !!s.requireUserConfirmAfter,
        })),
      };
      if (isEdit) {
        await approvalWorkflowsApi.update(id, payload);
        addToast("Onay akışı başarıyla güncellendi.");
      } else {
        await approvalWorkflowsApi.create(payload);
        addToast("Yeni onay akışı başarıyla oluşturuldu.");
      }
      setTimeout(() => navigate("/approval-workflows"), 1000);
    } catch (error) {
      console.error(error);
      addToast(
        error?.response?.data?.message || "Kaydedilirken hata oluştu.",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <AppPage
      title={isEdit ? "Onay Akışını Düzenle" : "Yeni Onay Akışı"}
      breadcrumbs={[
        { label: "Rovion", url: "" },
        { label: "Onay Akış Yönetimi", url: "/approval-workflows" },
        { label: isEdit ? "Düzenle" : "Yeni" },
      ]}
    >
      <CardContainer title="Genel Bilgiler" className="mb-4 p-3">
        <div className="mb-4">
          <Input
            label="Akış Adı"
            required
            value={form.name}
            onChange={(v) => updateField("name", v)}
            placeholder="Örn. Teklif Onay Akışı"
          />
        </div>

        <div className="mb-4">
          <p className="fw-semibold mb-1" style={{ fontSize: "0.875rem" }}>
            Uygulanacak Modül <span style={{ color: "#ef4444" }}>*</span>
          </p>
          <small className="text-muted d-block mb-2">
            Bu onay akışı hangi kayıt türü için geçerli olsun?
          </small>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "1rem",
            }}
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = form.entityType === opt.value;
              const disabled = isEdit && form.entityType !== opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    !disabled && updateField("entityType", opt.value)
                  }
                  style={{
                    border: `2px solid ${isSelected ? "#3b82f6" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "1rem",
                    backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    textAlign: "left",
                    transition: "all 0.15s",
                  }}
                >
                  <Icon
                    size={22}
                    color={isSelected ? "#2563eb" : "#64748b"}
                    style={{ marginBottom: 8 }}
                  />
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: isSelected ? "#1d4ed8" : "#1e293b",
                    }}
                  >
                    {opt.label}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#64748b",
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {opt.description}
                  </div>
                </button>
              );
            })}
          </div>
          {isEdit && (
            <small className="text-muted mt-2 d-block">
              Modül türü oluşturulduktan sonra değiştirilemez.
            </small>
          )}
        </div>

        <div>
          <Input
            label="Açıklama (isteğe bağlı)"
            value={form.description}
            onChange={(v) => updateField("description", v)}
            placeholder="Bu onay akışının amacını kısaca açıklayın"
          />
        </div>
      </CardContainer>

      <CardContainer
        title="Onay Adımları"
        className="mb-4 p-3"
        headerAction={
          <Button appearance="subtle" onClick={addStep} size="sm">
            <div className="p-1 rounded" title="Adım Ekle">
              {" "}
              <FiPlusCircle size={18} />
            </div>
          </Button>
        }
      >
        {form.steps.length === 0 ? (
          <div className="text-center py-5 bg-light rounded border-dashed">
            <p className="text-muted mb-3">
              Henüz adım yok. Onay akışını başlatmak için bir adım ekleyin.
            </p>
            <Button appearance="primary" onClick={addStep} icon={<FiPlus />}>
              Adım Ekle
            </Button>
          </div>
        ) : (
          <div className="mt-2">
            {/* Yatay Adım Navigasyonu */}
            <div
              className="d-flex align-items-center mb-4"
              style={{ overflowX: "auto", paddingBottom: "8px", gap: 0 }}
            >
              {form.steps.map((step, idx) => {
                const isActive = idx === activeStepIndex;
                const isComplete =
                  step.label?.trim() && (step.approvers || []).length > 0;
                return (
                  <div key={`tl-${idx}`} className="d-flex align-items-center">
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={(e) => handleDrop(e, idx)}
                      onClick={() => setActiveStepIndex(idx)}
                      className="px-3 py-2 rounded shadow-sm border d-flex align-items-center gap-2"
                      style={{
                        cursor: "pointer",
                        minWidth: "155px",
                        backgroundColor: isActive ? "#f0f9ff" : "#ffffff",
                        borderColor: isActive ? "#38bdf8" : "#e2e8f0",
                        transition: "all 0.2s",
                        userSelect: "none",
                        opacity: draggedStepIdx === idx ? 0.5 : 1,
                      }}
                    >
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: isActive ? "#0ea5e9" : "#64748b",
                          color: "#fff",
                          width: 24,
                          height: 24,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {step.stepNumber}
                      </span>
                      <span
                        style={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#0369a1" : "#475569",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 90,
                        }}
                      >
                        {step.label || `Adım ${step.stepNumber}`}
                      </span>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          minWidth: 8,
                          backgroundColor: isComplete ? "#22c55e" : "#ef4444",
                        }}
                        title={isComplete ? "Tamamlandı" : "Eksik alan var"}
                      />
                      {isActive && (
                        <div
                          className="ms-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStep(idx);
                          }}
                          style={{
                            padding: "3px",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#fee2e2")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                          title="Bu adımı sil"
                        >
                          <FiTrash2 color="#ef4444" size={13} />
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        width: 20,
                        height: 2,
                        backgroundColor: "#cbd5e1",
                        minWidth: 20,
                      }}
                    />
                  </div>
                );
              })}
              <Button
                appearance="subtle"
                onClick={addStep}
                size="sm"
                style={{ whiteSpace: "nowrap", height: 40 }}
              >
                <FiPlus /> Adım Ekle
              </Button>
            </div>

            {/* Aktif Adım İçeriği */}
            {form.steps[activeStepIndex] && (
              <div
                className="border border-primary-subtle rounded shadow-sm"
                style={{ backgroundColor: "#f8fafc" }}
              >
                <div
                  className="p-3 border-bottom d-flex align-items-center gap-2"
                  style={{ backgroundColor: "#eff6ff" }}
                >
                  <span className="badge bg-primary rounded-pill">
                    {form.steps[activeStepIndex].stepNumber}
                  </span>
                  <span className="fw-semibold text-primary">
                    {form.steps[activeStepIndex].label ||
                      `Adım ${form.steps[activeStepIndex].stepNumber}`}
                  </span>
                  <small className="text-muted ms-2">
                    Adımı düzenlemek için aşağıdaki alanları doldurun.
                    Sürükleyerek sırasını değiştirebilirsiniz.
                  </small>
                </div>
                <ActiveStepForm
                  step={form.steps[activeStepIndex]}
                  onChange={(updated) => updateStep(activeStepIndex, updated)}
                  allUsers={allUsers}
                  allProfiles={allProfiles}
                  entityType={form.entityType}
                />
              </div>
            )}
          </div>
        )}
      </CardContainer>

      {/* Page Actions - Sticky Bottom Bar */}
      <div
        className="d-flex align-items-center justify-content-between card card-body p-3 shadow-lg"
        style={{
          marginTop: "2rem",
          borderTop: "2px solid #e2e8f0",
          backgroundColor: "#fff",
        }}
      >
        <div>
          {isDirty && (
            <span
              className="d-flex align-items-center gap-1"
              style={{ color: "#f59e0b", fontSize: "0.82rem", fontWeight: 500 }}
            >
              <FiAlertCircle size={14} /> Kaydedilmemiş değişiklikler var
            </span>
          )}
        </div>
        <div className="d-flex gap-3">
          <Button
            appearance="danger-subtle"
            onClick={() => navigate("/approval-workflows")}
          >
            İptal Et
          </Button>
          <Button
            appearance="primary-animated"
            onClick={handleSave}
            isLoading={saving}
          >
            {isEdit ? "Güncelle" : "Oluştur"}
          </Button>
        </div>
      </div>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.appearance} />
        ))}
      </ToastContainer>
    </AppPage>
  );
}

export default withAuth(WorkflowForm);
